'use client'
import { ReactNode, useState } from 'react'
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react'
import clsx from 'clsx'

import { Collapse } from 'shared/ui/Collapse'
import { KEYBOARD_KEYS } from 'shared/ui/types'

import styles from './ExpandablePanel.module.scss'

interface ExpandablePanelProps {
  id: string
  title: string | ReactNode
  children: ReactNode | string
  disabled?: boolean
  expanded?: boolean
}

const ExpandablePanel = ({
  id,
  title,
  children,
  disabled,
  expanded = false
}: ExpandablePanelProps) => {
  const [open, setOpen] = useState(expanded)

  const toggleOpen = () => setOpen(prev => !prev)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === KEYBOARD_KEYS.ENTER || e.key === KEYBOARD_KEYS.SPACE) {
      e.preventDefault()
      toggleOpen()
    }
  }

  return (
    <div
      className={clsx(styles.wrapper, {
        [styles.disabled]: disabled,
        [styles.enabled]: !disabled
      })}
      {...{ disabled }}
    >
      <div
        className={styles.panelHeader}
        id={id}
        onClick={toggleOpen}
        aria-expanded={open}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        role='button'
        aria-label={open ? 'zwiń' : 'rozwiń'}
        aria-controls={`description-${id}`}
      >
        <h2 className={clsx(styles.panelTitle, 'expandable-panel_title')}>
          {title}
        </h2>
        <div className={styles.iconWrapper}>
          {open ? <IconChevronUp /> : <IconChevronDown />}
        </div>
      </div>
      <Collapse open={open} aria-hidden={!open} id={`description-${id}`}>
        <div className={styles.panelContent}>{children}</div>
      </Collapse>
    </div>
  )
}

export default ExpandablePanel
